/**
 * Analysis React Query hooks
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { analysisService } from '../services/analysisService';
import type { QuickAnalysisRequest, DeepAnalysisRequest } from '../../../entities/analysis';

export const analysisKeys = {
    job: (id: string) => ['analysis', 'job', id] as const,
    projectSummary: (id: string) => ['analysis', 'project-summary', id] as const,
};

/** Mutation to run a quick SAST analysis */
export function useQuickAnalysis() {
    return useMutation({
        mutationFn: (data: QuickAnalysisRequest) => analysisService.runQuickAnalysis(data),
    });
}

/** Mutation to start a deep ML analysis */
export function useDeepAnalysis() {
    return useMutation({
        mutationFn: (data: DeepAnalysisRequest) => analysisService.runDeepAnalysis(data),
    });
}

/** Poll the status of a deep analysis job */
export function useJobStatus(jobId: string, enabled = true) {
    return useQuery({
        queryKey: analysisKeys.job(jobId),
        queryFn: () => analysisService.getJobStatus(jobId),
        enabled: !!jobId && enabled,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            // Keep polling while queued or running
            if (status === 'queued' || status === 'running' || (status as string) === 'processing') return 3000;
            return false;
        },
    });
}

/** Fetch the analysis summary for a project */
export function useProjectAnalysisSummary(projectId: string, enabled = true) {
    return useQuery({
        queryKey: analysisKeys.projectSummary(projectId),
        queryFn: () => analysisService.getProjectSummary(projectId),
        enabled: !!projectId && enabled,
    });
}
